export interface CourtDto {
  Court_guid: string;
  Court_Email: string;
  Court_Address: string;
  Court_Name: string;
  Court_Phone?: string;
  Court_Fax?: string;
  Court_County?: string | null;
  Court_OffersOffDocket?: boolean | null;
  Court_OfRecord?: boolean | null;
  /**
   * How this court takes a filing, and where. Optional on the type because an older API returns
   * neither, and a court page that crashes on a stale server is the fault this rule exists for.
   */
  Court_FilingMethod?: CourtFilingMethod | null;
  Court_FilingAddress?: string | null;
  Court_FilingNote?: string | null;
  /** A JP court's precinct, '1'-'8' or 'countywide'. Null when not recorded. */
  Court_JpPrecinct?: string | null;
  /** The place within that precinct. Optional, and never set on a county-wide court. */
  Court_JpPlace?: string | null;
  /** Lloyd's court ratings (6 Oct 2026). Optional for the same stale-server reason as above. */
  Court_Traits?: string[];
  Court_TraitsOtherNegative?: string | null;
  Court_TraitsOtherPositive?: string | null;
  /** Traits the server read from the filing method or remote-appearance answer. Not editable. */
  Court_DetectedTraits?: { key: string; from: string }[];
  Court_Difficulty?: CourtDifficulty;
  Court_Active: boolean;
}

/**
 * One rating on a court or prosecutor. The lists come from GET /court/traits, which serves
 * backend/src/config/courtTraits.ts, so there is no second copy here to drift.
 */
export interface TraitDefinition {
  key: string;
  label: string;
  polarity: 'negative' | 'positive';
  weight: number;
}

export interface TraitLists {
  court: TraitDefinition[];
  prosecutor: TraitDefinition[];
  prosecutorContactPreferences: ProsecutorContactPreference[];
}

export type DifficultyLevel = 'none' | 'watch' | 'difficult';

export interface CourtDifficulty {
  score: number;
  level: DifficultyLevel;
  /** The negative traits behind the score, as labels. */
  reasons: string[];
}

export type ProsecutorContactPreference = 'email' | 'phone' | 'text';

export const PROSECUTOR_CONTACT_LABELS: Record<ProsecutorContactPreference, string> = {
  email: 'Email',
  phone: 'Phone',
  text: 'Text',
};

/**
 * How a court takes a filing. Mirrors `CourtFilingMethod` in
 * backend/src/utils/courts/courtFilingMethod.ts, which is the list the API validates against.
 * Change one and change the other, or the dropdown offers a value the server rejects.
 */
export type CourtFilingMethod = 'email' | 'website' | 'fax' | 'mail';

export const COURT_FILING_METHOD_LABELS: Record<CourtFilingMethod, string> = {
  email: 'Email',
  website: 'On the court’s website',
  fax: 'Fax',
  mail: 'Post',
};

/**
 * A JP court's precinct choices (Lloyd, 5 Oct 2026: "The max number of precincts is 8", and some JP
 * courts are county-wide). Mirrors JP_PRECINCT_VALUES in backend/src/utils/courts/courtPrecinct.ts,
 * which is the list the API validates against.
 */
export const JP_PRECINCT_OPTIONS: { value: string; label: string }[] = [
  ...['1', '2', '3', '4', '5', '6', '7', '8'].map((n) => ({ value: n, label: `Precinct ${n}` })),
  { value: 'countywide', label: 'County-wide (no precincts)' },
];

/** "Precinct 1, Place 2", "County-wide", or null when nothing is recorded. */
export function describeJpPrecinct(precinct?: string | null, place?: string | null): string | null {
  if (!precinct) return null;
  if (precinct === 'countywide') return 'County-wide';
  return place ? `Precinct ${precinct}, Place ${place}` : `Precinct ${precinct}`;
}

export interface ProsecutorDto {
  Prosecutor_guid: string;
  Prosecutor_Email: string;
  Prosecutor_Name: string;
  Prosecutor_Phone?: string | null;
  Prosecutor_Disposition?: string | null;
  Prosecutor_Traits?: string[];
  Prosecutor_TraitsOtherNegative?: string | null;
  Prosecutor_TraitsOtherPositive?: string | null;
  Prosecutor_PreferredContact?: ProsecutorContactPreference | null;
  Prosecutor_TextNumber?: string | null;
  Prosecutor_Active: boolean;
}

export interface FullCourtDto extends CourtDto {
  prosecutors: ProsecutorDto[];
}

export interface CreateCourtDto {
  Court_Email: string;
  Court_Address: string;
  Court_Name: string;
  Court_Phone?: string;
  Court_Fax?: string;
  Court_Active: boolean;
}

export interface CreateProsecutorDto {
  Prosecutor_Email: string;
  Prosecutor_Name: string;
  Prosecutor_Phone?: string | null;
  Prosecutor_Disposition?: string | null;
  Prosecutor_Traits?: string[];
  Prosecutor_TraitsOtherNegative?: string | null;
  Prosecutor_TraitsOtherPositive?: string | null;
  Prosecutor_PreferredContact?: ProsecutorContactPreference | null;
  Prosecutor_TextNumber?: string | null;
  Court_guid: string;
  Prosecutor_Active: boolean;
}

export interface FullProsecutorDto extends ProsecutorDto {
  court?: {
    Court_guid: string;
    Court_Name: string;
    Court_Email: string;
    Court_Address: string;
    Court_Phone?: string;
    Court_Fax?: string;
  };
}

export interface SearchFilters {
  Court_Name: string;
  Court_Address: string;
  Court_Email: string;
  Court_Phone: string;
  Court_Fax: string;
  search: string;
}
